import { useFormik } from "formik";
import { useHistory } from "react-router";
import {
  formatBreakPeriodsForApi,
  formatWeekPeriodsForApi,
} from "Requests/Formatters/Units";
import {
  createUnits,
  deleteImage,
  deleteParking,
  deleteTransport,
  editUnit,
  showUnit,
} from "Requests/Units";
import { createToast } from "Utils/toastFunc";
import { useQuery, useQueryClient } from "react-query";

import { useEffect, useState } from "react";
import { unitValidationSchema } from "./components/Form/schemas";

const weekDays = [1, 2, 3, 4, 5, 6];

const createUnitPeriod = (day_id: any) => ({
  day_id,
  start_id: "",
  end_id: "",
});

const createReportPeriod = (day_id: any) => ({
  day_id,
  start_id: "",
  end_id: "",
});

const createBreakPeriod = () => ({
  start_id: "",
  end_id: "",
  starts_at: "",
  ends_at: "",
});

const arrayValues = {
  images: [],
  covenant_ids: [],
  category_ids: [],
  channels_ids: [],
  transports: [],
  parkings: [],
  unit_periods: weekDays.map((dayId) => createUnitPeriod(dayId)),
  break_periods: [createBreakPeriod()],
  report_periods: weekDays.map((dayId) => createReportPeriod(dayId)),
  collect_periods: weekDays.map((dayId) => createReportPeriod(dayId)),
};

const booleansValues = {
  accessibility: false,
  wheelchair: false,
  next_subway: false,
  open_unit: true,
  coming_soon: false,
  toxic_exam: false,
  parking: false,
  collection_point: false,
};

const INITIAL_STATE = {
  name: "",
  image_map: "",
  addresses: {
    neighborhood: "",
    street: "",
    number: "",
    complement: "",
    cep: "",
    latitude: "",
    longitude: "",
    city_id: "",
  },
  calendar_id: "",
  motion_code: "",
  cashier: "",
  region_id: "",
  exam_schedule: "",
  result_schedule: "",
  unmet_products: "",
  facilities: "",
  reference: "",
  parking_desk: "",
  type: "",
  professional_code: "",
  slug: "",
  ...booleansValues,
  ...arrayValues,
};

type UnitImage = { id?: number; apiId?: number; name?: string };

type ParkingItem = {
  id?: number;
  addresses?: { id?: number };
};

type TransportItem = {
  id?: number;
};

const getImageId = (image: UnitImage) => image.apiId ?? image.id;

const getParkingId = (parking: ParkingItem) =>
  parking.id ?? parking.addresses?.id;

const getTransportId = (transport: TransportItem) => transport.id;

const getInitialState = () => {
  return INITIAL_STATE;
};

type WeekPeriod = {
  day_id: number;
  start_id: string;
  end_id: string;
};

type ApiWeekPeriod = {
  day_id?: number;
  start_id?: string | number;
  end_id?: string | number;
};

const toPeriodFieldValue = (value: string | number | undefined) =>
  value != null && value !== "" ? String(value) : "";

const normalizeWeekPeriods = (
  periods: ApiWeekPeriod[] | undefined,
  createEmpty: (dayId: number) => WeekPeriod
): WeekPeriod[] => {
  const defaults = weekDays.map((dayId) => createEmpty(dayId));

  if (!Array.isArray(periods) || periods.length === 0) {
    return defaults;
  }

  const periodByDay = new Map(
    periods
      .filter((item) => item?.day_id)
      .map((item) => [
        Number(item.day_id),
        {
          day_id: Number(item.day_id),
          start_id: toPeriodFieldValue(item.start_id),
          end_id: toPeriodFieldValue(item.end_id),
        },
      ])
  );

  return defaults.map((empty) => periodByDay.get(empty.day_id) ?? empty);
};

const getDataFromEditing = (values: any) => {
  return {
    accessibility: values.accessibility,
    wheelchair: values.wheelchair,
    next_subway: values.next_subway,
    open_unit: values.open_unit,
    coming_soon: values.coming_soon,
    toxic_exam: values.toxic_exam,
    parking: values.parking,
    collection_point: values.collection_point,
    unit_periods: normalizeWeekPeriods(values.unit_periods, createUnitPeriod),
    break_periods: values.break_periods?.length
      ? values.break_periods
      : [createBreakPeriod()],
    report_periods: normalizeWeekPeriods(
      values.report_periods,
      createReportPeriod
    ),
    collect_periods: normalizeWeekPeriods(
      values.collect_periods,
      createReportPeriod
    ),
    image_map: values.image_map ?? "",
    images: [],
    covenant_ids: values.covenants,
    category_ids: values.categories,
    channels_ids: values.channels,
    transports: values.transports,
    parkings: values.parkings,
    addresses: {
      id: values.addresses?.id ?? "",
      neighborhood: values.addresses.neighborhood,
      street: values.addresses.street,
      number: values.addresses.number,
      complement: values.addresses.complement,
      cep: values.addresses.cep,
      latitude: values.addresses.latitude,
      longitude: values.addresses.longitude,
      city_id:
        values.addresses.cities?.id ??
        values.addresses.city_id ??
        values.addresses.id,
      city: values.addresses.cities.city,
    },
    name: values.name,
    calendar_id: values.calendar_id,
    motion_code: values.motion_code,
    cashier: values.cashier,
    region_id: values.region_id,
    exam_schedule: values.exam_schedule ?? "",
    result_schedule: values.result_schedule ?? "",
    unmet_products: values.unmet_products ?? "",
    facilities: values.facilities ?? "",
    reference: values.reference ?? "",
    parking_desk: values.parking_desk ?? "",
    type: values.type,
    professional_code: values.professional_code ?? "",
    slug: values.slug,
  };
};

export const useUnit = (unitId: string | null) => {
  const history = useHistory();

  const [showApiImages, setShowApiImages] = useState<UnitImage[]>([]);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["unit", unitId],
    queryFn: () => showUnit(unitId),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    enabled: !!unitId,
  });

  const formatPeriodValue = (key: string, value: unknown) => {
    if (
      key === "unit_periods" ||
      key === "report_periods" ||
      key === "collect_periods"
    ) {
      return formatWeekPeriodsForApi(
        value as typeof INITIAL_STATE.unit_periods
      );
    }

    if (key === "break_periods") {
      return formatBreakPeriodsForApi(
        value as typeof INITIAL_STATE.break_periods
      );
    }

    return value;
  };

  const formatObjectFormData = (values: typeof INITIAL_STATE) => {
    const notMappedValues = [
      "collect_periods",
      "report_periods",
      "unit_periods",
      "break_periods",
      "parkings",
      "transports",
      "addresses",
    ];

    const skipArrayValues = ["images"];

    const skipObjectValues = ["image_map"];

    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (notMappedValues.includes(key)) {
        const payloadValue =
          key === "unit_periods" ||
          key === "report_periods" ||
          key === "collect_periods" ||
          key === "break_periods"
            ? formatPeriodValue(key, value)
            : value;
        return formData.append(key, JSON.stringify(payloadValue));
      }

      if (Array.isArray(value)) {
        return value.forEach((item: any) => {
          if (skipArrayValues.includes(key))
            return formData.append(`${key}[]`, item);
          if (item.id) return formData.append(`${key}[]`, item.id);
        });
      }

      if (key === "image_map" && typeof value === "string") {
        return formData.append(key, "");
      }

      if (typeof value === "object" && !skipObjectValues.includes(key)) {
        return formData.append(key, JSON.stringify(value));
      }

      if (value !== "") formData.append(key, value as any);
    });

    formData.delete("addresses.address_id");

    return formData;
  };

  const createUnit = async (payload: FormData) => {
    await createUnits(payload);
    createToast("success", "Unidade criada com sucesso");
    history.push("/unidades");
  };

  const editUnitRequest = async (payload: FormData) => {
    await editUnit(payload, unitId);
    createToast("success", "Unidade atualizada com sucesso");
    history.push("/unidades");
  };

  const handleSubmit = async (values: typeof INITIAL_STATE) => {
    try {
      const payload = formatObjectFormData(values);

      if (unitId) {
        return await editUnitRequest(payload);
      }
      await createUnit(payload);
    } catch (error: any) {
      if (error?.response?.data?.errors) {
        Object.entries(
          error.response.data.errors as Record<string, string[]>
        ).forEach(([field, fieldErrors]) => {
          formik.setFieldError(field, fieldErrors[0]);
        });
      }
      createToast("error", "Erro ao criar unidade");
    }
  };

  const INITIALVALUES =
    unitId && data?.data.id
      ? getDataFromEditing(data?.data)
      : getInitialState();

  const formik = useFormik({
    initialValues: INITIALVALUES,
    onSubmit: handleSubmit,
    validationSchema: unitValidationSchema,
    enableReinitialize: true,
    validateOnChange: false,
  });

  const handleDeleteImage = async (identifier: string | number) => {
    const numericId = Number(identifier);
    const isApiImage = !Number.isNaN(numericId);

    try {
      if (isApiImage) {
        await deleteImage(identifier);

        queryClient.setQueryData(["unit", unitId], (old: any) => {
          if (!old?.data?.images) return old;

          return {
            ...old,
            data: {
              ...old.data,
              images: old.data.images.filter(
                (image: { id: number }) => image.id !== numericId
              ),
            },
          };
        });
      }

      formik.setFieldValue(
        "images",
        formik.values.images.filter((image: UnitImage) => {
          if (isApiImage) return getImageId(image) !== numericId;
          return image.name !== identifier;
        })
      );

      if (isApiImage) {
        createToast("success", "Imagem removida com sucesso");
      }
    } catch {
      createToast("error", "Erro ao remover imagem");
    }
  };

  const handleDeleteParking = async (identifier: string | number) => {
    const numericId = Number(identifier);
    const isApiParking = !Number.isNaN(numericId);
    try {
      if (isApiParking) {
        await deleteParking(identifier);

        queryClient.setQueryData(["unit", unitId], (old: any) => {
          if (!old?.data?.parkings) return old;

          return {
            ...old,
            data: {
              ...old.data,
              parkings: old.data.parkings.filter(
                (parking: ParkingItem) => getParkingId(parking) !== numericId
              ),
            },
          };
        });
      }

      formik.setFieldValue(
        "parkings",
        formik.values.parkings.filter(
          (parking: ParkingItem) => getParkingId(parking) !== numericId
        )
      );

      if (isApiParking) {
        createToast("success", "Estacionamento removido com sucesso");
      }
    } catch {
      createToast("error", "Erro ao remover estacionamento");
    }
  };

  const handleDeleteTransport = async (identifier: string | number) => {
    const numericId = Number(identifier);
    const isApiTransport = !Number.isNaN(numericId);

    try {
      if (isApiTransport) {
        await deleteTransport(identifier);

        queryClient.setQueryData(["unit", unitId], (old: any) => {
          if (!old?.data?.transports) return old;

          return {
            ...old,
            data: {
              ...old.data,
              transports: old.data.transports.filter(
                (transport: TransportItem) =>
                  getTransportId(transport) !== numericId
              ),
            },
          };
        });
      }

      formik.setFieldValue(
        "transports",
        formik.values.transports.filter(
          (transport: TransportItem) => getTransportId(transport) !== numericId
        )
      );

      if (isApiTransport) {
        createToast("success", "Transporte removido com sucesso");
      }
    } catch {
      createToast("error", "Erro ao remover transporte");
    }
  };

  useEffect(() => {
    if (data?.data?.images) {
      setShowApiImages(data?.data?.images);
    }
  }, [data?.data?.images]);

  return {
    formik,
    hasUnitDetails: !!unitId && !!data?.data,
    isLoading,
    handleDeleteImage,
    handleDeleteParking,
    handleDeleteTransport,
    showApiImages,
    hasData: !!data?.data.id,
  };
};
